/**
 * Modern Navbar Enhancement Script
 * Handles scroll effects, smooth animations, and improved UX
 */

(function() {
  'use strict';

  // Configuration
  const config = {
    stickyHeaderOffset: 100,
    scrollThrottle: 16, // ~60fps
    mobileBreakpoint: 768,
    enableAnimations: !document.body.dataset.disableAnimations
  };

  // Utility: Throttle function for performance
  function throttle(func, limit) {
    let inThrottle;
    return function() {
      const args = arguments;
      const context = this;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  }

  // Utility: Debounce function
  function debounce(func, wait) {
    let timeout;
    return function() {
      const context = this;
      const args = arguments;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  /**
   * Enhanced Sticky Header with Scroll Effects
   */
  class StickyHeaderManager {
    constructor() {
      this.header = document.getElementById('SiteHeader');
      this.headerWrapper = document.querySelector('.header-sticky-wrapper');
      this.body = document.body;
      this.lastScrollY = window.scrollY;
      this.scrollDirection = 'down';
      this.isSticky = false;

      if (!this.header) return;

      this.init();
    }

    init() {
      // Only initialize if header is set to sticky
      if (this.header.dataset.sticky !== 'true') return;

      // Add scroll listener with throttle for performance
      window.addEventListener('scroll', throttle(() => this.handleScroll(), config.scrollThrottle));

      // Initial check
      this.handleScroll();
    }

    handleScroll() {
      const currentScrollY = window.scrollY;
      const headerHeight = this.header.offsetHeight;

      // Determine scroll direction
      this.scrollDirection = currentScrollY > this.lastScrollY ? 'down' : 'up';

      // Add/remove stuck class based on scroll position
      if (currentScrollY > config.stickyHeaderOffset) {
        if (!this.isSticky) {
          this.makeSticky();
        }

        // Hide header on scroll down, show on scroll up
        if (this.scrollDirection === 'down' && currentScrollY > headerHeight * 2) {
          this.hideHeader();
        } else if (this.scrollDirection === 'up') {
          this.showHeader();
        }
      } else {
        if (this.isSticky) {
          this.removeSticky();
        }
      }

      this.lastScrollY = currentScrollY;
    }

    makeSticky() {
      this.header.classList.add('site-header--stuck');
      this.body.classList.add('header-is-sticky');
      this.isSticky = true;

      // Add padding to prevent content jump
      if (this.headerWrapper) {
        this.headerWrapper.style.paddingTop = this.header.offsetHeight + 'px';
      }
    }

    removeSticky() {
      this.header.classList.remove('site-header--stuck');
      this.body.classList.remove('header-is-sticky');
      this.isSticky = false;

      // Remove padding
      if (this.headerWrapper) {
        this.headerWrapper.style.paddingTop = '';
      }
    }

    hideHeader() {
      if (config.enableAnimations) {
        this.header.style.transform = 'translateY(-100%)';
      }
    }

    showHeader() {
      this.header.style.transform = 'translateY(0)';
    }
  }

  /**
   * Enhanced Mobile Menu
   */
  class MobileMenuManager {
    constructor() {
      this.drawer = document.getElementById('NavDrawer');
      this.openButtons = document.querySelectorAll('.js-drawer-open-nav');
      this.closeButtons = document.querySelectorAll('.js-drawer-close');
      this.overlay = document.querySelector('.drawer-overlay');
      this.body = document.body;

      this.init();
    }

    init() {
      if (!this.drawer) return;

      // Enhanced open animation
      this.openButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.openDrawer();
        });
      });

      // Enhanced close animation
      this.closeButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeDrawer();
        });
      });

      // Close on overlay click
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.closeDrawer());
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.drawer.classList.contains('is-visible')) {
          this.closeDrawer();
        }
      });

      // Handle collapsible menus
      this.initCollapsibleMenus();
    }

    openDrawer() {
      this.drawer.classList.add('is-visible');
      this.body.classList.add('js-drawer-open', 'js-drawer-open-nav');

      // Trap focus in drawer
      this.trapFocus();
    }

    closeDrawer() {
      this.drawer.classList.remove('is-visible');
      this.body.classList.remove('js-drawer-open', 'js-drawer-open-nav');
    }

    trapFocus() {
      const focusableElements = this.drawer.querySelectorAll(
        'a[href], button, textarea, input, select'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      this.drawer.addEventListener('keydown', (e) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      });

      // Focus first element
      if (firstElement) firstElement.focus();
    }

    initCollapsibleMenus() {
      const toggleButtons = this.drawer.querySelectorAll('.mobile-nav__toggle-btn');

      toggleButtons.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const parent = btn.closest('.mobile-nav__item');
          const isActive = parent.classList.contains('is-active');

          // Close other open menus at same level
          const siblings = Array.from(parent.parentElement.children);
          siblings.forEach(sibling => {
            if (sibling !== parent) {
              sibling.classList.remove('is-active');
            }
          });

          // Toggle current menu
          parent.classList.toggle('is-active', !isActive);
        });
      });
    }
  }

  /**
   * Enhanced Dropdown Menus (Desktop)
   */
  class DropdownManager {
    constructor() {
      this.dropdowns = document.querySelectorAll('.site-nav__details');
      this.megaMenus = document.querySelectorAll('.site-nav--is-megamenu');
      this.isTouchDevice = 'ontouchstart' in window;

      this.init();
    }

    init() {
      if (!this.dropdowns.length) return;

      this.dropdowns.forEach(dropdown => {
        const hoverEnabled = dropdown.dataset.hover === 'true' && !this.isTouchDevice;

        if (hoverEnabled) {
          this.initHoverMenu(dropdown);
        } else {
          this.initClickMenu(dropdown);
        }
      });

      // Close dropdowns when clicking outside
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.site-nav__details')) {
          this.closeAllDropdowns();
        }
      });

      // Close dropdowns on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAllDropdowns();
        }
      });
    }

    initHoverMenu(dropdown) {
      let hoverTimeout;

      dropdown.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout);
        this.openDropdown(dropdown);
      });

      dropdown.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          this.closeDropdown(dropdown);
        }, 200);
      });
    }

    initClickMenu(dropdown) {
      const summary = dropdown.querySelector('summary');

      if (summary) {
        summary.addEventListener('click', (e) => {
          // Let browser handle details/summary
          // But add our custom class for styling
          setTimeout(() => {
            if (dropdown.open) {
              dropdown.classList.add('is-open');
            } else {
              dropdown.classList.remove('is-open');
            }
          }, 10);
        });
      }
    }

    openDropdown(dropdown) {
      dropdown.setAttribute('open', '');
      dropdown.classList.add('is-open');
      dropdown.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    }

    closeDropdown(dropdown) {
      dropdown.removeAttribute('open');
      dropdown.classList.remove('is-open');
      dropdown.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }

    closeAllDropdowns() {
      this.dropdowns.forEach(dropdown => this.closeDropdown(dropdown));
    }
  }

  /**
   * Search Enhancement
   * Handles both inline search input and search overlay
   */
  class SearchManager {
    constructor() {
      this.searchToggles = document.querySelectorAll('.js-search-header');
      this.searchContainer = document.querySelector('.site-header__search-container');
      this.searchInput = document.querySelector('.search__input');
      this.searchClose = document.querySelector('.btn--close-search');
      this.inlineSearchInput = document.querySelector('.inline-search-input');

      this.init();
    }

    init() {
      if (!this.searchContainer) return;

      // Handle all search toggle buttons (mobile icon, inline input focus)
      this.searchToggles.forEach(toggle => {
        // For buttons, handle click
        if (toggle.tagName === 'BUTTON') {
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.openSearch();
          });
        }
        // For inline search input, handle focus
        else if (toggle.tagName === 'INPUT') {
          toggle.addEventListener('focus', (e) => {
            this.openSearch();
          });
          toggle.addEventListener('click', (e) => {
            e.preventDefault();
            this.openSearch();
          });
        }
      });

      // Close button
      if (this.searchClose) {
        this.searchClose.addEventListener('click', (e) => {
          e.preventDefault();
          this.closeSearch();
        });
      }

      // Close on escape
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.searchContainer.classList.contains('is-active')) {
          this.closeSearch();
        }
      });

      // Close on screen click
      const screen = document.querySelector('.predictive__screen');
      if (screen) {
        screen.addEventListener('click', () => {
          this.closeSearch();
        });
      }
    }

    openSearch() {
      this.searchContainer.classList.add('is-active');
      document.body.classList.add('js-drawer-open', 'js-drawer-open--search');

      // Focus the main search input in the overlay
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.focus();
          // Copy value from inline input if it exists
          if (this.inlineSearchInput && this.inlineSearchInput.value) {
            this.searchInput.value = this.inlineSearchInput.value;
          }
        }
      }, 100);
    }

    closeSearch() {
      this.searchContainer.classList.remove('is-active');
      document.body.classList.remove('js-drawer-open', 'js-drawer-open--search');
    }
  }

  /**
   * Scroll Performance Optimization
   */
  class ScrollPerformance {
    constructor() {
      this.isScrolling = false;
      this.scrollTimeout = null;

      this.init();
    }

    init() {
      window.addEventListener('scroll', () => {
        if (!this.isScrolling) {
          document.body.classList.add('is-scrolling');
          this.isScrolling = true;
        }

        clearTimeout(this.scrollTimeout);

        this.scrollTimeout = setTimeout(() => {
          document.body.classList.remove('is-scrolling');
          this.isScrolling = false;
        }, 100);
      });
    }
  }

  /**
   * Active Link Highlighter
   */
  class ActiveLinkManager {
    constructor() {
      this.currentPath = window.location.pathname;
      this.navLinks = document.querySelectorAll('.site-nav__link, .mobile-nav__link');

      this.init();
    }

    init() {
      this.navLinks.forEach(link => {
        const linkPath = new URL(link.href, window.location.origin).pathname;

        if (linkPath === this.currentPath ||
            (this.currentPath.startsWith(linkPath) && linkPath !== '/')) {
          link.classList.add('site-nav__link--active');
        }
      });
    }
  }

  /**
   * Responsive Image Loading in Mega Menu
   */
  class MegaMenuImageLoader {
    constructor() {
      this.megaMenuImages = document.querySelectorAll('.megamenu__collection-image img');
      this.init();
    }

    init() {
      if (!this.megaMenuImages.length) return;

      // Use Intersection Observer for lazy loading
      if ('IntersectionObserver' in window) {
        const imageObserver = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const img = entry.target;
              if (img.dataset.src) {
                img.src = img.dataset.src;
                img.removeAttribute('data-src');
              }
              imageObserver.unobserve(img);
            }
          });
        });

        this.megaMenuImages.forEach(img => imageObserver.observe(img));
      }
    }
  }

  /**
   * Initialize all components
   */
  function init() {
    // Wait for DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initComponents);
    } else {
      initComponents();
    }
  }

  function initComponents() {
    new StickyHeaderManager();
    new MobileMenuManager();
    new DropdownManager();
    new SearchManager();
    new ScrollPerformance();
    new ActiveLinkManager();
    new MegaMenuImageLoader();

    // Dispatch custom event for other scripts
    document.dispatchEvent(new CustomEvent('navbar:enhanced'));
  }

  // Start initialization
  init();

})();
